import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { useCallback, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Icon from 'components/global/Icon';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { getAddressByGeocode, getPlaceDetails } from 'components/maps/utils/mapHelpers';

import useAlert from 'hooks/useAlert';

import AppLogger from 'utils/AppLogger';

import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';

import Button from './Button';
import MapField from './MapField';

const MapModal = NiceModal.create(({ title, value, restrictions, formMethods, parseAddress }) => {
	const modal = useModal();

	const [loading, setLoading] = useState(false);
	const [data, setData] = useState(value);

	const { showError } = useAlert();
	const { t } = useTranslation();

	const mapRef = useRef(null);

	const handleValueChange = (value) => {
		setData(value);
	};

	const onSelect = useCallback(async () => {
		setLoading(true);
		try {
			let location;
			if (!data?.lat || !data?.lng) {
				const { lat, lng } = mapRef.current.getCenter();
				location = { lat: lat(), lng: lng() };
			} else location = { lat: data.lat, lng: data.lng };
			const placeDetails = await getPlaceDetails(location);

			if (restrictions?.country?.isNotDifferent) {
				const addressDetails = getAddressByGeocode(placeDetails);

				if (
					addressDetails?.country &&
					addressDetails.country.toLowerCase() !== restrictions?.country?.code?.toLowerCase()
				) {
					showError({
						message: t('errorMsg.countryNotMatch', {
							country: !!restrictions?.country?.code && t(`country.${restrictions.country.code}`)
						})
					});
				} else {
					parseAddress(placeDetails, data?.text ?? placeDetails?.formatted_address);
					modal.remove();
				}
			} else {
				parseAddress(placeDetails, data?.text ?? placeDetails?.formatted_address);
				modal.remove();
			}
		} catch (error) {
			AppLogger.error(error);
		} finally {
			setLoading(false);
		}
	}, [data, t, restrictions]);

	const onCancel = () => {
		modal.remove();
	};

	return (
		<Modal fullwidth>
			<ModalTitle>
				<span className="typography" style={{ display: 'flex', alignItems: 'center' }}>
					<Icon icon={faLocationDot} />
					<span className="mx-1" />
					<Typography variant="p1" fontWeight="500">
						{title}
					</Typography>
				</span>
			</ModalTitle>
			<ModalContent>
				<FormProvider {...formMethods}>
					<div style={{ height: '70dvh' }}>
						<MapField ref={mapRef} value={data} setValue={handleValueChange} restrictions={restrictions} />
					</div>
				</FormProvider>
			</ModalContent>
			<ModalActions>
				<Button variant="outline" onClick={onCancel} label="cancel" />
				<Button onClick={onSelect} label="select" loading={loading} />
			</ModalActions>
		</Modal>
	);
});

export default MapModal;
